import posthogEvents from "@app/config/posthog/posthog-events";
import UploadDatasetModal from "@app/routes/dataset/upload/UploadDatasetModal";
import ModalHeader from "@lib/common/components/modals/ModalHeader";
import { notifications } from "@lib/notification/notification";
import { Modal } from "@mantine/core";
import type { DropzoneProps, FileRejection } from "@mantine/dropzone";
import { Dropzone, type FileWithPath } from "@mantine/dropzone";
import { noop } from "lodash-es";
import { usePostHog } from "posthog-js/react";
import { useCallback, useState } from "react";

const TOO_MANY_FILES_ERROR_CODE = "too-many-files";

function exceedsFileLimit(rejections: FileRejection[]) {
  return rejections.some(rejection => rejection.errors.some(error => error.code === TOO_MANY_FILES_ERROR_CODE));
}

export function BaseDatasetDropzone({
  maxSize = 5 * 1024 * 1024,
  accept = { "text/csv": [".csv", ".Csv", ".CSV"], "text/plain": [".txt", ".Txt", ".TXT"] },
  multiple = false,
  children,
  ...props
}: Omit<DropzoneProps, "onDrop" | "onDropAny" | "onReject">) {
  const posthog = usePostHog();
  const [inputFiles, setInputFiles] = useState<FileWithPath[]>([]);

  const closeModal = useCallback(() => setInputFiles([]), []);

  // react-dropzone keeps the first file when too many are dropped, so the accepted and
  // rejected files have to be judged together to refuse the whole batch.
  const onDropAny = (files: FileWithPath[], rejections: FileRejection[]) => {
    if (exceedsFileLimit(rejections)) {
      notifications.error("You can only upload one dataset at a time.");
      return;
    }

    if (rejections.length > 0) {
      notifications.error("Files uploaded must have a .csv or .txt extension, and a max size of 5MB");
    }

    if (files.length <= 0) {
      if (rejections.length <= 0) {
        notifications.error("No dataset was selected.");
      }
      return;
    }

    posthog.capture(posthogEvents.dataset_drop_zone.file_upload);
    setInputFiles(files);
  };

  return (
    <>
      <Dropzone onDropAny={onDropAny} onDrop={noop} maxSize={maxSize} accept={accept} multiple={multiple} {...props}>
        {children}
      </Dropzone>

      {inputFiles?.length > 0 && (
        <Modal
          size={"auto"}
          opened={Boolean(inputFiles)}
          onClose={closeModal}
          withCloseButton={false}
          closeOnEscape={false}
          withOverlay
        >
          <ModalHeader title="Upload dataset" onClose={closeModal}></ModalHeader>
          <UploadDatasetModal file={inputFiles[0]} onClose={closeModal} />
        </Modal>
      )}
    </>
  );
}
